import { api } from './client.js';

export const joinStatsApi = {
  dashboard: (p: {
    from: string;
    to: string;
    minUsers: string | number;
    warnRate: string | number;
    mode: EvaluationMode;
  }) =>
    api.get<JoinDashboard>(
      `/join-stats/dashboard?${new URLSearchParams({
        from: p.from,
        to: p.to,
        minUsers: String(p.minUsers),
        warnRate: String(p.warnRate),
        mode: p.mode,
      }).toString()}`,
    ),

  /** 신규가입 상태 점검 - "지금 이상이 있는가" 하나만 답한다. */
  health: () => api.get<JoinHealth>('/join-stats/health'),

  /** 요일별 대시보드 (원본 join_member_week/dashboard.php) */
  weekday: (p: { from?: string; to?: string; zThreshold: string | number; week: number }) =>
    api.get<WeekDashboard>(
      `/join-stats/weekday?${new URLSearchParams({
        ...(p.from ? { from: p.from } : {}),
        ...(p.to ? { to: p.to } : {}),
        zThreshold: String(p.zThreshold),
        week: String(p.week),
      }).toString()}`,
    ),
};

/**
 * 신규가입 전환 대시보드 (원본 marketing/join_member/dashboard.php).
 * 계산은 서버(joinstats.dashboard.ts)가 끝내서 내려준다 - 원본도 PHP 가
 * 다 계산해 Vue 에 넘겼다.
 */
export type JoinStatus = 'ok' | 'warn' | 'danger' | 'tracking' | 'low';
export type EvaluationMode = 'standard' | 'hourly_average';

export interface JoinHourRow {
  date: string;
  hour: number;
  hourLabel: string;
  weekday: number;
  joinP: number;
  joinM: number;
  joinTotal: number;
  joinSocial: number;
  joinNormal: number;
  memberP: number;
  memberM: number;
  memberTotal: number;
  social: number;
  normal: number;
  naver: number;
  google: number;
  twitter: number;
  banana: number;
  slim: number;
  conversion: number | null;
  pcConversion: number | null;
  mobileConversion: number | null;
  socialConversion: number | null;
  normalConversion: number | null;
  hourAvgConversion: number | null;
  hourAvgJoinTotal: number;
  gap: number;
  status: JoinStatus;
  statusLabel: string;
  flags: string[];
}

export interface JoinDailyRow {
  date: string;
  joinTotal: number;
  memberTotal: number;
  joinP: number;
  joinM: number;
  memberP: number;
  memberM: number;
  social: number;
  normal: number;
  dangerCount: number;
  warnCount: number;
  conversion: number | null;
}

export interface JoinHourBucket {
  hour: number;
  joinTotal: number;
  memberTotal: number;
  conversion: number | null;
  inflowRate: number | null;
  avgJoinTotal: number;
}

export interface JoinSummary {
  joinP: number;
  joinM: number;
  joinTotal: number;
  joinSocial: number;
  joinNormal: number;
  memberP: number;
  memberM: number;
  memberTotal: number;
  social: number;
  normal: number;
  naver: number;
  google: number;
  twitter: number;
  banana: number;
  slim: number;
  conversion: number | null;
  pcConversion: number | null;
  mobileConversion: number | null;
  socialConversion: number | null;
  normalConversion: number | null;
  dangerCount: number;
  warnCount: number;
  okCount: number;
  lowSampleCount: number;
  trackingIssueCount: number;
  hourCount: number;
  issueCount: number;
}

export interface JoinDashboard {
  rows: JoinHourRow[];
  daily: JoinDailyRow[];
  hourBucket: JoinHourBucket[];
  issues: JoinHourRow[];
  summary: JoinSummary;
  filters: { from: string; to: string; minUsers: number; warnRate: number; mode: EvaluationMode };
  meta: { criticalRate: number; rangeDayCount: number; hourlyAverageTolerance: number };
}

/**
 * 요일별 대시보드 (원본 marketing/join_member_week/dashboard.php).
 * 판정 기준이 '요일 × 시간' 이라 날짜별 화면과 다른 값들이 붙는다
 * (기준 표본 수·중앙값·표준편차·Z).
 */
export type WeekStatus = 'ok' | 'warn' | 'danger' | 'tracking' | 'low' | 'spike' | 'pending';

export interface WeekHourRow {
  date: string;
  hour: number;
  hourLabel: string;
  dow: number;
  dowLabel: string;
  baselineLabel: string;
  joinP: number;
  joinM: number;
  joinTotal: number;
  joinSocial: number;
  joinNormal: number;
  memberP: number;
  memberM: number;
  memberTotal: number;
  social: number;
  normal: number;
  naver: number;
  google: number;
  twitter: number;
  banana: number;
  slim: number;
  conversion: number | null;
  pcConversion: number | null;
  mobileConversion: number | null;
  socialConversion: number | null;
  normalConversion: number | null;
  baselineCount: number;
  baselineAvg: number;
  baselineMedian: number;
  baselineStddev: number;
  baselineConversion: number | null;
  baselineZ: number | null;
  baselineRatio: number | null;
  isPartial: boolean;
  gap: number;
  status: WeekStatus;
  statusLabel: string;
  flags: string[];
}

export interface WeekDayRow {
  date: string;
  dow: number;
  dowLabel: string;
  joinP: number;
  joinM: number;
  joinTotal: number;
  joinSocial: number;
  joinNormal: number;
  memberP: number;
  memberM: number;
  memberTotal: number;
  social: number;
  normal: number;
  naver: number;
  google: number;
  twitter: number;
  banana: number;
  slim: number;
  conversion: number | null;
  pcConversion: number | null;
  mobileConversion: number | null;
  daySampleCount: number;
  dayAvg: number;
  dayMedian: number;
  dayStddev: number;
  dayAvgConversion: number | null;
  dayZ: number | null;
  dayRatio: number | null;
  dangerCount: number;
  warnCount: number;
  spikeCount: number;
  pendingCount: number;
  isPartial: boolean;
  status: WeekStatus;
  statusLabel: string;
  hourly: WeekHourRow[];
}

export interface DowHourStat {
  dow: number;
  hour: number;
  sampleCount: number;
  avgJoinTotal: number;
  medianJoinTotal: number;
  stddevJoinTotal: number;
  avgConversion: number | null;
}

export interface WeekFocus {
  available: boolean;
  date: string;
  dow: number;
  dowLabel: string;
  hour: number;
  hourLabel: string;
  joinTotal: number;
  memberTotal: number;
  conversion: number | null;
  sampleCount: number;
  avg: number;
  median: number;
  stddev: number;
  avgConversion: number | null;
  z: number | null;
  ratio: number | null;
}

export interface WeekDashboard {
  days: WeekDayRow[];
  hourBucket: JoinHourBucket[];
  dowHourStats: DowHourStat[];
  focus: WeekFocus;
  issues: WeekHourRow[];
  summary: JoinSummary & { spikeCount: number; pendingCount: number; dayCount: number };
  filters: { from: string; to: string; zThreshold: number; week: number };
  meta: {
    week: number;
    weekLabel: string;
    baselineConversion: number | null;
    rangeDayCount: number;
    tolerance: number;
    zThreshold: number;
    zWarnThreshold: number;
    baselineStart: string;
    baselineEnd: string;
    baselineDayCount: number;
    baselineSampleDays: number;
  };
}

/**
 * 신규가입 상태 점검. 요일별/날짜별 화면이 조사용이라면 이쪽은 감시용이다.
 * 판정 대상이 유입이 아니라 **실제 신규가입** 이고, 기준은 최근 N주 같은
 * 요일의 같은 시각까지 누적 중앙값이다 (server joinstats.health.ts 참고).
 */
export type HealthStatus = 'ok' | 'warn' | 'danger' | 'spike' | 'low' | 'pending';

export interface HealthChannel {
  key: string;
  label: string;
  today: number;
  expected: number;
  ratio: number | null;
  status: HealthStatus;
}

export interface HealthDay {
  date: string;
  dow: number;
  dowLabel: string;
  joins: number;
  expected: number;
  ratio: number | null;
  status: HealthStatus;
  statusLabel: string;
}

export interface JoinHealth {
  asOf: {
    date: string;
    hour: number;
    hourLabel: string;
    dow: number;
    dowLabel: string;
    complete: boolean;
  };
  status: HealthStatus;
  statusLabel: string;
  reason: string;
  today: { joins: number; inflow: number; conversion: number | null };
  baseline: {
    expected: number;
    low: number;
    high: number;
    sigma: number;
    sampleCount: number;
    weeks: number;
  };
  ratio: number | null;
  lastWeek: { date: string; joins: number; ratio: number | null } | null;
  outage: { hours: number; fromHourLabel: string; toHourLabel: string; expected: number } | null;
  channels: HealthChannel[];
  recent: HealthDay[];
  lastAnomaly: HealthDay | null;
  meta: { baselineWeeks: number; minExpected: number; outageHours: number };
}
